/**
 * Adjust one member's chat-storage limit (#758).
 *
 * ── The first question is not a number ──────────────────────────────────
 *
 * It asks WHO DECIDES this member's limit — follow the role default, or
 * give them one of their own — and the amounts live INSIDE the second
 * choice. That shape is the design, not a flourish.
 *
 * The two states are different and a number cannot express both. Clearing
 * the limit (writing NULL) makes the member inherit, so the next change to
 * the role default reaches them. Typing a number that happens to equal
 * today's default does NOT: it pins them, and that change silently skips
 * them while the admin believes they were reset. A dialog that opened on a
 * number field would have "put her back to normal" typed as 500 almost
 * every time, which is precisely the wrong write. That is also why the
 * save button RENAMES itself — handing a limit back is a different act
 * from saving one, and the button should say which one is about to happen.
 *
 * ── It shows the consequence before the save, not after ─────────────────
 *
 * Three things an admin cannot work out from a number on its own, and each
 * has burnt somebody:
 *
 *   - What it does to THIS member. Handing a limit back is not
 *     automatically a kindness: a member on 1 GB storing 460 MB who is
 *     dropped to the 500 MB default lands at 92% and can barely upload.
 *   - What it does to the ORGANISATION. One limit moves the total
 *     promised across every member, shown here against the quota.
 *
 *     A DIFFERENT QUESTION from the storage bar's over-allocation mark,
 *     which stopped reading this total (an earlier version of this line
 *     said it did — scoped review round 7, finding 6). This dialog asks
 *     "have we promised more than we own?", which is about LIMITS. The
 *     bar asks "can the promise that is still unspent actually fit in
 *     what is free?", which is about HEADROOM. Both can be true alone:
 *     raise a member from 1 GB to 2 GB while she stores 3 GB and this
 *     dialog's total crosses the pool while the bar stays quiet, because
 *     her headroom is still nothing. Neither is wrong; they answer different
 *     things, and the copy on each says which. (The example holds only
 *     while the NEW limit is still below what she stores — raise her
 *     past it and she has headroom again, and the bar can fire too.)
 *   - That lowering below what they store deletes nothing. Said plainly
 *     and always, not only as a warning when it applies — an admin who has
 *     never seen it cannot know it is safe.
 */
import { useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { storageApi, type MemberChatStorageRow } from '../../client/storage'
import { nameFromEmail } from '@papan/core-ui/ui'
import { formatBytes } from '../budget/bits'
import { roleLabel } from './shared'

const MB = 1024 * 1024
/** The three the design offers; anything else is typed. */
const PRESETS = [500 * MB, 1024 * MB, 2048 * MB]

/** A typed amount is committable only as a whole, non-negative MB count.
 *
 *  Decimal digits ONLY, not bare `Number()` (full review 3C, finding 8):
 *  `Number('0x10')` is 16 and `Number('1e5')` is 100000, and both pass
 *  `Number.isInteger`, so a paste could commit a limit the field never
 *  showed. Bounded too — the value is multiplied by 1 MiB, and an
 *  unbounded one leaves `Number.MAX_SAFE_INTEGER` behind, which is the one
 *  range `parseChatStorageLimit` rejects server-side with a 400. 16 TB is
 *  past any real drive and still exact in a double. */
const MAX_TYPED_MB = 16 * 1024 * 1024
function wholeMb(v: string): number | null {
  const t = v.trim()
  if (t === '' || !/^\d+$/.test(t)) return null
  const n = Number(t)
  return Number.isSafeInteger(n) && n >= 0 && n <= MAX_TYPED_MB ? n : null
}

export function AdjustChatLimitDialog({
  tenantId, member, roleDefaultBytes, quotaBytes, promisedBytes, onClose, onSaved,
}: {
  tenantId: string
  member: MemberChatStorageRow
  /** What this member's ROLE resolves to today. */
  roleDefaultBytes: number | null
  /** The organisation's ceiling, for the allocation line. */
  quotaBytes?: number
  /** Promised across all members today, this member included. */
  promisedBytes?: number | null
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  // The SAME derivation the member list uses two components away. Three
  // spellings of one person could otherwise appear on one card: "John Doe"
  // in the row, "john.doe" in this dialog's title, "john.doe" in the
  // pending request above it (full review 2, finding 12).
  const name = nameFromEmail(member.email)
  const [own, setOwn] = useState(member.hasOwnLimit)
  // Held as TEXT while editing: a number state turns a half-typed "" into
  // 0, and 0 is a real limit (a hard block), not an empty field.
  const [custom, setCustom] = useState<string>(String(Math.round((member.limitBytes ?? roleDefaultBytes ?? 500 * MB) / MB)))
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)

  const typedMb = wholeMb(custom)
  const valid = !own || typedMb !== null
  // `null` while the typed amount is INVALID, not 0. `(typedMb ?? 0)`
  // previewed an empty field as a saved limit of zero: clear the box to
  // retype and the outcome panel turned red claiming "their limit
  // becomes 0 B, below the 460 MB they already store", and the
  // allocation line struck through the real total and dropped it by a
  // gigabyte — a consequence panel describing a write that is not
  // happening, with only the small "enter a whole number" note to say
  // otherwise (full review 4C, finding 5).
  const nextLimit = own ? (typedMb === null ? null : typedMb * MB) : roleDefaultBytes
  const free = nextLimit === null ? null : nextLimit - member.totalBytes
  const pct = nextLimit && nextLimit > 0 ? Math.min(999, Math.round((member.totalBytes / nextLimit) * 100)) : 0
  // `limitBytes === 0` is a block, so the "currently using" bar is FULL,
  // not empty — it used to draw an empty track under "Currently using
  // 50.0 MB of 0 B" (full review 3C, finding 3).
  const usedPctNow = member.limitBytes === null
    ? 0
    : member.limitBytes === 0
      ? 100
      : Math.min(100, Math.round((member.totalBytes / member.limitBytes) * 100))

  // The organisation's commitment before and after. Only meaningful when
  // the page handed us both figures and neither side is unlimited — an
  // allocation line that silently dropped an unlimited member would be a
  // number the admin could not reconcile with the bar.
  const currentLimit = member.limitBytes
  // `valid` too: an invalid typed amount has no `nextLimit`, so the
  // projection would strike through the real total and show a number
  // from a save that is not happening (full review 4C, finding 5).
  const canProject = valid && typeof promisedBytes === 'number' && typeof quotaBytes === 'number'
    && currentLimit !== null && nextLimit !== null
  const nextPromised = canProject ? promisedBytes! - currentLimit! + nextLimit! : null

  const save = async () => {
    setSaving(true); setFailed(false)
    try {
      // The note rides with the write so the member's notification can
      // carry the admin's reason — it is the only place they see it.
      await storageApi.setMemberChatStorageLimit(tenantId, member.email, own ? (typedMb ?? 0) * MB : null, note.trim() || undefined)
      onSaved()
      onClose()
    } catch {
      setFailed(true); setSaving(false)
    }
  }

  // Chosen by the OUTCOME, never by which option got you there. The same
  // end state — limit 500 MB, 520 MB stored — used to produce two
  // different sentences depending on whether the admin arrived by handing
  // the limit back or by typing 500, and one of them clamped the overage
  // to "0 B left" when they were in fact 20 MB over. What happens to the
  // member does not depend on the route the admin took.
  //
  // What DOES differ between the two options is what happens to them LATER
  // when the role default moves, and that is already said on each option's
  // own hint — repeating it here is what crowded this line into describing
  // the path instead of the result.
  const over = free !== null && free < 0
  // ZERO is its own outcome. The tight branch said "their limit becomes
  // 0 B and they are at 0% — only 0 B left. They keep every file, but
  // can upload little until they clear space": they are at 100% not 0%,
  // they can upload NOTHING not little, and clearing space cannot change
  // it. Three wrong statements in one sentence, and the admin read it as
  // a tight-but-workable limit (full review 4C, finding 4). The branch
  // had already fixed this same sentence in the member's own view and in
  // the role dialog; this one was missed.
  // NOT gated on `own`. The same commit that added this made
  // `roleDefaultBytes` resolve to 0 for a membership on any role outside
  // the six shipped ones — and "follow the role default" is the option
  // selected by default for a member with no own limit. So the non-own
  // path reached a 0 ceiling and took the `tight` branch instead: "their
  // limit becomes 0 B and they are at 0% — only 0 B left … can upload
  // little until they clear space", the exact three-wrong-statements
  // sentence this guard exists to replace (scoped review of the
  // convergence pass, finding 4). An admin setting a role override to
  // 0 MB gets there too.
  const blocksEverything = nextLimit === 0
  // EXACTLY AT IT IS ITS OWN OUTCOME, and this was the last surface still
  // saying otherwise. The gate refuses at `total + net > limit`, so
  // `free === 0` means not one byte — and `tight` claimed "they are at
  // 100% — only 0 B left … can upload little until they clear space".
  //
  // Every other surface had already been moved to `>= limit`: the member's
  // own `MyFigure` (whose comment says "the surfaces have to agree about
  // who is in trouble"), the admin's `MemberRow`, the in-chat banner, and
  // the server's own `noHeadroom`. Round 43 raised this exact case as
  // A-tight and marked it FIX; what actually landed was the zero-limit
  // branch and the member-facing copy, and the equality cell on THIS card
  // was missed — so an admin reading "tight but workable" was deciding
  // from the one surface that still said it (final full review, B1).
  //
  // Not folded into `over`: that sentence says the limit lands "below the
  // {used} they already store", which is false when it lands exactly on
  // it. Its own string instead.
  const atLimit = !blocksEverything && free === 0
  const tight = !blocksEverything && free !== null && free > 0 && free < 100 * MB
  // UNLIMITED is its own sentence. `free` is null there, and the fine
  // branch formatted `free ?? 0`, so following an unlimited role default
  // promised "They will have 0 B free." on the same card whose option hint
  // correctly said "No limit today" (full review 3C, finding 4).
  const outcome = !valid
    // Nothing to describe yet — the field is mid-edit.
    ? null
    : nextLimit === null
      ? t('settings.storage.chatStorageOutcomeUnlimited')
      : blocksEverything
        // THREE sentences, discriminated by WHERE the 0 comes from — and
        // the discriminator is the ROLE DEFAULT'S VALUE, not which radio
        // is selected. Keying it on `own` fixed one half and left the
        // other: an admin typing 0 for a member whose role default is
        // ALSO 0 was told "or let them follow the role default", and
        // handing it back blocks them again (scoped review round 36, A1).
        //
        // `roleDefaultBytes === null` is UNLIMITED, so handing back
        // genuinely is the remedy there; a positive default likewise. It
        // is only a 0 default — reachable by an admin setting one, or by
        // a role outside the six shipped ones, which this branch
        // deliberately resolves to 0 — that makes the advice a no-op.
        //
        // The sentence is written to be true in BOTH cells that reach
        // it. An earlier draft said the role default is 0 "as well",
        // which has no antecedent when the admin did not type a zero:
        // under `!own` there is only ONE zero, and it is the default
        // (round 37, B2).
        //
        // Only TWO sentences in the end, because `!own` makes `nextLimit`
        // the role default — so `!own && blocksEverything` IS
        // `roleDefaultBytes === 0`, and the "follow the role default"
        // branch cannot be reached while blocked. A per-role variant
        // added for this round was dead on arrival and went with it.
        //
        // On round 36 (A2, B3) two Chinese locales pointed at the
        // Defaults-by-role screen by a LABEL that is not printed on its
        // button. The fix was to drop the label, NOT the destination:
        // `…BlockedBoth` still sends the admin there ("or raise that
        // default"), because when the role default is what holds the
        // member at 0, that screen is the only place to lift it. It names
        // the ACTION instead, which survives the button being renamed.
        // Every role a membership can actually hold has a row on that
        // screen — all three role writers (invite create, invite edit,
        // PATCH members/role) accept exactly the six keys of
        // `DEFAULT_ROLE_CHAT_STORAGE`, which is the list the screen
        // renders. A role outside it resolves to 0 here as a defence, not
        // as a reachable state, so this copy is not written for it.
        ? t(roleDefaultBytes === 0
            ? 'settings.storage.chatStorageOutcomeBlockedBoth'
            : 'settings.storage.chatStorageOutcomeBlocked', { role: roleLabel(member.role) })
        : over
          ? t('settings.storage.chatStorageOutcomeOver', { limit: formatBytes(nextLimit), used: formatBytes(member.totalBytes) })
          : atLimit
            ? t('settings.storage.chatStorageOutcomeAtLimit', { limit: formatBytes(nextLimit) })
          : tight
            ? t('settings.storage.chatStorageOutcomeTight', { limit: formatBytes(nextLimit), pct, free: formatBytes(free!) })
            : t('settings.storage.chatStorageOutcomeFine', { free: formatBytes(Math.max(0, free ?? 0)) })
  const outcomeBad = blocksEverything || over || atLimit || tight

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div className="relative z-10 max-w-xl w-full max-h-[85vh] flex flex-col ws-glass-modal ws-anim-modal-in">

        <div className="flex items-start gap-2.5 px-6 pt-5 pb-3.5 themed-border border-b">
          <div className="grow min-w-0">
            <h3 className="text-[17px] font-semibold themed-text truncate">
              {t('settings.storage.chatStorageLimitFor', { name })}
            </h3>
            {/* THREE sites, not the two the fix commit claimed: this
                header printed the raw id one line above the two sentences
                that were corrected, so a `noaccess` member read
                "bob@x · noaccess" directly above "follow the No access
                default" (scoped review of full review 43, finding 5). */}
            <p className="text-xs themed-text-muted mt-0.5 truncate">{member.email} · {roleLabel(member.role)}</p>
          </div>
          {/* The badge is the row's own state, repeated here so the admin
              can see what they are changing FROM. */}
          {member.hasOwnLimit && (
            <span className="shrink-0 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full themed-bg-tertiary themed-text-secondary">
              {t('settings.storage.chatStorageCustomBadge')}
            </span>
          )}
        </div>

        <div className="px-6 py-4 overflow-y-auto min-h-0 flex-1 space-y-4">

          <div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs themed-text-muted">{t('settings.storage.chatStorageCurrentlyUsing')}</span>
              <span className="text-[15px] font-semibold themed-text">{formatBytes(member.totalBytes)}</span>
              {member.limitBytes !== null && (
                <span className="text-xs themed-text-muted">
                  {t('settings.storage.chatStorageOfLimit', { limit: formatBytes(member.limitBytes) })}
                </span>
              )}
            </div>
            <div className="h-2.5 rounded-full bg-black/20 overflow-hidden mt-1.5">
              <div className="h-full bg-blue-500" style={{ width: `${usedPctNow}%` }} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-[13px] font-semibold themed-text">{t('settings.storage.chatStorageWhoDecidesShort')}</div>

            <label className={`flex gap-2.5 items-start p-2.5 rounded-lg cursor-pointer ${!own ? 'bg-blue-500/10' : ''}`}>
              <input type="radio" name="who" checked={!own} onChange={() => setOwn(false)} className="mt-1" />
              <span className="min-w-0">
                <span className="block text-sm themed-text">
                  {/* The LABEL, so this reads "follow the Drive admin
                      default" and not "follow the admin default" — and
                      never "follow the noaccess default" (43C B9). */}
                  {t('settings.storage.chatStorageFollowRole', { role: roleLabel(member.role) })}
                </span>
                <span className="block text-xs themed-text-muted mt-0.5 leading-relaxed">
                  {t('settings.storage.chatStorageFollowHint', {
                    amount: roleDefaultBytes === null ? t('settings.storage.chatStorageUnlimited') : formatBytes(roleDefaultBytes),
                  })}
                </span>
              </span>
            </label>

            <div className={`p-2.5 rounded-lg ${own ? 'bg-blue-500/10' : ''}`}>
              <label className="flex gap-2.5 items-start cursor-pointer">
                <input type="radio" name="who" checked={own} onChange={() => setOwn(true)} className="mt-1" />
                <span className="min-w-0">
                  <span className="block text-sm themed-text">{t('settings.storage.chatStorageOwnLimit')}</span>
                  <span className="block text-xs themed-text-muted mt-0.5 leading-relaxed">
                    {t('settings.storage.chatStorageOwnHint')}
                  </span>
                </span>
              </label>
              {/* Dimmed rather than removed when following: the admin can
                  see what they would be choosing without the dialog
                  changing height under them. */}
              <div className={`flex flex-wrap items-center gap-2 mt-2.5 pl-7 ${own ? '' : 'opacity-45'}`}>
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => { setOwn(true); setCustom(String(Math.round(p / MB))) }}
                    className={`text-[13px] px-3 py-1.5 rounded-lg border ${own && (typedMb ?? -1) * MB === p ? 'border-blue-500 themed-text' : 'themed-border themed-text-secondary'}`}
                  >
                    {formatBytes(p)}
                  </button>
                ))}
                <span className="w-px h-6 themed-bg-tertiary mx-0.5" />
                <label htmlFor="chat-limit-custom" className="text-xs themed-text-muted">
                  {t('settings.storage.chatStorageCustomMb')}
                </label>
                <input
                  id="chat-limit-custom"
                  type="number"
                  min={0}
                  step={1}
                  value={custom}
                  onChange={(e) => { setOwn(true); setCustom(e.target.value) }}
                  aria-label={t('settings.storage.chatStorageCustomMb')}
                  className="w-24 text-[13px] px-2.5 py-1.5 rounded-lg themed-border border themed-bg-tertiary themed-text"
                />
                <span className="text-xs themed-text-muted">MB</span>
              </div>
            </div>
          </div>

          {/* What the save will actually do — and NOTHING while the typed
              amount is invalid, because then no save is being described.
              The CARD goes too: withholding only the sentence left an
              empty bordered grey box where it had been, since the two
              other children are both conditional as well (scoped review
              of the convergence pass, finding 5). */}
          {outcome !== null && (
          <div className="rounded-xl themed-border border themed-bg-secondary px-4 py-3 space-y-2.5">
            <div className="flex items-start gap-2.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={outcomeBad ? '#f59e0b' : '#22c55e'} strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="shrink-0 mt-0.5">
                {outcomeBad
                  ? <><path d="M12 3l9.5 16.5h-19z" /><path d="M12 10v4M12 17h.01" /></>
                  : <path d="M20 7L10 17l-5-5" />}
              </svg>
              <p className="text-[13px] themed-text leading-relaxed">{outcome}</p>
            </div>
            {!own && member.hasOwnLimit && (
              <p className="text-xs themed-text-muted pl-6">{t('settings.storage.chatStorageBadgeComesOff')}</p>
            )}
            {canProject && (
              <>
                <div className="h-px themed-bg-tertiary" />
                <div className="flex items-baseline gap-2 text-xs">
                  <span className="themed-text-muted">{t('settings.storage.chatStorageAllocatedAcross')}</span>
                  <span className="grow" />
                  {nextPromised !== promisedBytes && (
                    <span className="themed-text-muted line-through">{formatBytes(promisedBytes!)}</span>
                  )}
                  <span className="text-[13px] font-semibold themed-text">{formatBytes(nextPromised!)}</span>
                  <span className="themed-text-muted">{t('settings.storage.chatStorageOfPool', { pool: formatBytes(quotaBytes!) })}</span>
                </div>
              </>
            )}
          </div>
          )}

          <div>
            <label htmlFor="chat-limit-note" className="block text-xs themed-text-muted mb-1.5">
              {t('settings.storage.chatStorageNoteLabel')}
            </label>
            <input
              id="chat-limit-note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('settings.storage.chatStorageNotePlaceholder')}
              className="w-full text-[13px] px-3 py-2 rounded-lg themed-border border themed-bg-tertiary themed-text"
            />
          </div>

          {failed && <p className="text-xs text-rose-500">{t('settings.storage.chatStorageSaveFailed')}</p>}
        </div>

        <div className="flex items-center gap-2.5 px-6 py-3.5 themed-border border-t">
          <span className="grow text-[11px] themed-text-muted">{t('settings.storage.chatStorageWillBeNotified')}</span>
          {/* Same as the role dialog: say why Save is off rather than just
              greying it (full review 2, finding 6). */}
          {!valid && (
            <span className="text-[12px] text-rose-400 mr-auto self-center">
              {t('settings.storage.chatStorageInvalidAmount')}
            </span>
          )}
          <button type="button" onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg themed-border border themed-text">
            {t('common:cancel')}
          </button>
          {/* The label names the act: handing a limit back is not the same
              as saving one, and only one of them is about to happen. */}
          <button
            type="button"
            onClick={save}
            disabled={saving || !valid}
            className="text-[13px] px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60"
          >
            {saving
              ? t('settings.storage.chatStorageSaving')
              : t(own ? 'settings.storage.chatStorageSaveLimit' : 'settings.storage.chatStorageHandBack')}
          </button>
        </div>
      </div>
    </div>
  )
}
